import React from 'react';
import PropTypes from 'prop-types';
import { FormControlLabel, Checkbox, IconButton } from '@mui/material';
import { Delete, Edit, Close, Check } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import * as PropertyTypes from 'constants/propertyType';
import { TextField } from '@totalsoft/rocket-ui';
import { DateTime } from '@ts-private/rocket-extensions';
import { emptyString } from 'utils/constants';
import { StyledTableBodyCell, StyledTableRow } from 'components/common/Styles';
import { ADMIN_PROPERTIES_WRITE } from 'constants/rightsCodes';
import { usePermission } from 'providers/RightsHooks';

const PropertyDetailListItemComponent = ({
  index,
  propertyDetail,
  propertyTypeId,
  validation,
  onPropertyChanged,
  onDelete,
  onCancel,
  onEdit
}) => {
  const { t } = useTranslation();
  const canWrite = usePermission(ADMIN_PROPERTIES_WRITE);

  return (
    <StyledTableRow>
      {propertyDetail.inEdit ? (
        <>
          <StyledTableBodyCell>
            <TextField
              isNumeric
              fullWidth
              label={t('Property.OrderNo')}
              value={propertyDetail.orderNo || emptyString}
              onChange={event => onPropertyChanged('orderNo', event, index)}
              decimalScale={0}
              error={!isValid(validation[index].orderNo)}
              helperText={getErrors(validation[index].orderNo)}
            />
          </StyledTableBodyCell>
          <StyledTableBodyCell>
            <TextField
              fullWidth
              label={t('Property.Code')}
              value={propertyDetail.code || emptyString}
              onChange={event => onPropertyChanged('code', event, index)}
            />
          </StyledTableBodyCell>
          {propertyTypeId === PropertyTypes.textPropertyType && (
            <StyledTableBodyCell>
              <TextField
                required
                fullWidth
                label={t('Property.PropertyValue')}
                value={propertyDetail.propertyValue || emptyString}
                onChange={event => onPropertyChanged('propertyValue', event, index)}
                error={!isValid(validation[index].propertyValue)}
                helperText={getErrors(validation[index].propertyValue)}
              />
            </StyledTableBodyCell>
          )}
          {propertyTypeId === PropertyTypes.numericPropertyType && (
            <StyledTableBodyCell>
              <TextField
                required
                isNumeric
                fullWidth
                label={t('Property.PropertyValue')}
                value={propertyDetail.propertyValue || emptyString}
                onChange={event => onPropertyChanged('propertyValue', event, index)}
                error={!isValid(validation[index].propertyValue)}
                helperText={getErrors(validation[index].propertyValue)}
              />
            </StyledTableBodyCell>
          )}
          {propertyTypeId === PropertyTypes.dataPropertyType && (
            <StyledTableBodyCell>
              <DateTime
                required
                name='validFrom'
                value={propertyDetail.propertyValue}
                timeFormat={false}
                label={t('Property.PropertyValue')}
                onChange={event => onPropertyChanged('propertyValue', event, index)}
                error={!isValid(validation[index].propertyValue)}
                helperText={getErrors(validation[index].propertyValue)}
              />
            </StyledTableBodyCell>
          )}
          <StyledTableBodyCell>
            <TextField
              fullWidth
              label={t('Property.Description')}
              value={propertyDetail.description || emptyString}
              onChange={event => onPropertyChanged('description', event, index)}
            />
          </StyledTableBodyCell>
          <StyledTableBodyCell>
            <FormControlLabel
              control={
                <Checkbox
                  checked={propertyDetail.inactive}
                  onChange={event => onPropertyChanged('inactive', event.target.checked, index)}
                />
              }
            />
          </StyledTableBodyCell>
        </>
      ) : (
        <>
          <StyledTableBodyCell>{propertyDetail.orderNo || emptyString}</StyledTableBodyCell>
          <StyledTableBodyCell>{propertyDetail.code || emptyString}</StyledTableBodyCell>
          {propertyTypeId === PropertyTypes.textPropertyType && (
            <StyledTableBodyCell>{propertyDetail.propertyValue || emptyString}</StyledTableBodyCell>
          )}
          {propertyTypeId === PropertyTypes.numericPropertyType && (
            <StyledTableBodyCell>{propertyDetail.propertyValue || emptyString}</StyledTableBodyCell>
          )}
          {propertyTypeId === PropertyTypes.dataPropertyType && <StyledTableBodyCell>{propertyDetail.propertyValue}</StyledTableBodyCell>}
          <StyledTableBodyCell>{propertyDetail.description || emptyString}</StyledTableBodyCell>
          <StyledTableBodyCell>
            <>{propertyDetail.inactive === true ? <Check color='success' /> : <Close />}</>
          </StyledTableBodyCell>
        </>
      )}
      <StyledTableBodyCell align='right'>
        {propertyDetail.inEdit ? (
          <IconButton
            size='small'
            color='primary'
            tooltip={t('Tooltips.Cancel')}
            onClick={() => {
              onCancel(index);
            }}
          >
            <Close />
          </IconButton>
        ) : (
          <IconButton size='small' color='primary' tooltip={t('Tooltips.Edit')} onClick={() => onEdit(index)} disabled={!canWrite}>
            <Edit />
          </IconButton>
        )}
        <IconButton size='small' color='primary' tooltip={t('Tooltips.Delete')} onClick={() => onDelete(index)} disabled={!canWrite}>
          <Delete />
        </IconButton>
      </StyledTableBodyCell>
    </StyledTableRow>
  );
};

PropertyDetailListItemComponent.propTypes = {
  index: PropTypes.number.isRequired,
  propertyDetail: PropTypes.object.isRequired,
  propertyTypeId: PropTypes.number.isRequired,
  validation: PropTypes.object,
  onPropertyChanged: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  onEdit: PropTypes.func
};

export default PropertyDetailListItemComponent;
